import React from 'react';
import { get, isEmpty, isNumber, minBy } from 'lodash';
import { SITE_URL } from 'site-modules/shared/pages/seo';
import { ConsumerReviewsModel, ConsumerReviewsPaths } from 'client/data/models/consumer-reviews';
import {
  EditorialReviewModel,
  buildReviewPath,
  buildAuthorPath,
  buildPreProdContentPath,
  buildRatedByPath,
} from 'client/data/models/editorial-review';
import { VehicleDetailsModel, buildWhatsNewPath } from 'client/data/models/mmy-details';
import { buildVideosPath, MediaModel } from 'client/data/models/media';
import { VehiclePreloader } from 'site-modules/shared/utils/core-page/vehicle-preloader-v2';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { NativeAdsCreativeConfigModel } from 'client/data/models/native-ads-creative-config';
import {
  buildVehiclePath,
  buildPopularStylesPath,
  buildSubmodelsPath,
  buildMakeModelDefaultYear,
  buildUsedTmvPricingPath,
  getUsedBaseStylePath,
  buildEvTestedDataPath,
  buildFuelCostsPath,
  buildMmyModelFamilyIdsPath,
  buildModelFamilyIdsModelYearsPath,
  FeatureSpecsPaths,
  VehicleModel,
  buildMMYGenericFeaturesPath,
  buildForSaleYearsPath,
} from 'client/data/models/vehicle-v2';
import { LinkWidgetsModel, getPageLinksPath } from 'client/data/models/link-widgets';
import { SafetyModel, buildSafetyRatingsPathByParams } from 'client/data/models/safety';
import { getRepairPalDataPath, ReliabilityModel } from 'client/data/models/reliability';
import { buildMakeArticlesPath, EditorialModel } from 'client/data/models/editorial';
import { InventoryModel } from 'client/data/models/inventory';
import { EvInsightsModel, EvInsightsPaths, INVENTORY_CODE } from 'client/data/models/ev-insights';
import { InventoryAggregateModel, buildMMYSubmodelTypeAmountsPath } from 'client/data/models/inventory-aggregate';
import { URL_CONTEXT_PATH, UrlModel } from 'client/data/models/url';
import { getModelState } from 'client/data/luckdragon/model';
import { buildCoreBreadcrumbs } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { getCoreUrlPattern } from 'site-modules/shared/utils/core-page/url-pattern';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { getCarVinSchema, getCorePageCarSchema } from 'client/seo/schemas/car';
import { getReviewUrl } from 'site-modules/shared/utils/core-link-constructor';
import {
  buildMMYSubmodelTrimPhotosPathWithParams,
  buildSubmodelEvoxPhotoPath,
  PhotosModel,
} from 'client/data/models/photos';
import { buildSimilarVehiclesCrossShopPath, CoreResearchModel } from 'client/data/models/core-research';
import { getTreehouseEligiblePath, TreehouseModel } from 'client/data/models/treehouse';
import { buildAllPhotosPath, PhotoflipperModel } from 'client/data/models/photoflipper';
import { getEvTaxCreditAmountPath, getWizardIncentivesPath, IncentiveModel } from 'client/data/models/incentives';
import { formatPriceString } from 'client/site-modules/shared/utils/price-utils';
import { EdmundsNewCarsNoAdhesion } from 'client/site-modules/shared/pages/decorators/edmunds-new-cars-no-adhesion';
import { appendReviewSchema } from 'client/seo/schemas/review';
import { getImageSchema } from 'client/seo/schemas/image';
import { getMediaImageUrl } from 'client/utils/image-helpers';
import {
  getCreativeConfigAdPathByVehicle,
  setAdsListToPreload,
  siteServedAdsCriticalCss,
} from 'client/site-modules/shared/components/native-ad/utils/utils';
import { PageModel } from 'client/data/models/page';
import {
  INCENTIVES_AD,
  BUILD_PRICE_AD,
  PRICING_AD,
  EAS_LISTING_AD,
} from 'client/site-modules/shared/components/native-ad/utils/constants';
import { CmsModel } from 'client/data/models/cms';
import { setCriticalCss } from 'client/actions/critical-css';
import { corePageDecorator } from 'site-modules/shared/utils/core-page/core-page-decorator-v2';
import { CORE_GALLERY_SIZE, VEHICLE_MILEAGE_PER_YEAR } from 'client/site-modules/core-page/utils/constants';
import { EV_TESTED_DATA_START_DATE } from 'site-modules/shared/constants/range-and-cost';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import { getUsedVehiclePriceRangeFromModelState } from 'site-modules/shared/utils/core-page/pricing';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { CAR_NEWS_LANDING_CONTENT_PATH } from 'client/site-modules/shared/constants/editorial/editorial';
import { OLD_REVIEW_FORMAT_LAST_YEAR } from 'site-modules/shared/constants/editorial-review';
import { getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { getPreloadHeroImageAsset } from 'client/site-modules/core-page/utils/get-preload-hero-image-asset';
import {
  CORE_SRP_INVENTORY_COUNT,
  getInitialSearchFilter,
  getUnknownLocationSearchFilter,
} from 'site-modules/core-page/utils/core-srp';
import { getFilterFromLocation } from 'site-modules/shared/utils/inventory/search-filter';
import { getQuery } from 'client/utils/location';
import { CPO_CARS } from 'site-modules/shared/utils/cpo-message';

import { UsedCore } from './used-core';

const getPageOptions = params => ({
  name: params.isReviewSubpage ? 'used_model_core_review' : 'used_model_core',
  category: 'used_model_core',
  chunkName: 'used_model_core',
  preload: (store, props) => {
    const { isReviewSubpage, year } = params;
    const query = getQuery(props.location);
    const { mobile, visitor } = store.getState();
    const visitorLocation = get(visitor, 'location', '');
    const isInternational = isUnknownZip(visitorLocation);

    const state = store.getState();
    const isABTestEas8839Enabled =
      ExperimentUtil.getForcedOrAssignedRecipeName({
        state,
        campaignName: 'eas-8839',
        defaultVal: 'ctrl',
      }) === 'chal';

    const preloader = new VehiclePreloader(store, { ...props, params }, { isSubpage: false });

    preloader.set(URL_CONTEXT_PATH, UrlModel, {
      urlPattern: getCoreUrlPattern(params.year, params.submodel, isReviewSubpage && 'review'),
    });

    return preloader.load().then(modelState => {
      const makeModelSubmodelYear = modelState.get(buildVehiclePath(params), VehicleModel);
      const {
        make: { slug: make },
        model: { slug: model, modelLinkCode },
        submodels: { slug: submodel },
      } = makeModelSubmodelYear;

      const requestParams = { make, model, submodel, year };
      const vehicleParams = { make, model, year, ...(params.submodel ? { submodel: params.submodel } : {}) };
      const photoParams = {
        ...requestParams,
        submodel: makeModelSubmodelYear.submodels.name,
      };

      preloader.resolve(FeatureSpecsPaths.buildPartialFeaturesPath(requestParams), VehicleModel);
      preloader.resolve(buildMakeModelDefaultYear(vehicleParams), VehicleModel);
      preloader.resolve(buildMMYSubmodelTypeAmountsPath(params), InventoryAggregateModel);
      preloader.resolve(getUsedBaseStylePath(params), VehicleModel);

      // cost-to-drive
      preloader.resolve(buildPopularStylesPath(vehicleParams), VehicleModel);
      // core highlights,ev insights,trims-comparator link
      preloader.resolve(buildPopularStylesPath(getParamsFromVehicle(makeModelSubmodelYear)), VehicleModel);

      // core-highlights,reliability ratings
      preloader.resolve(getRepairPalDataPath({ makeSlug: make, modelSlug: model, year }), ReliabilityModel);

      preloader.resolve(buildReviewPath(vehicleParams), EditorialReviewModel);
      preloader.resolve(buildAuthorPath(vehicleParams), EditorialReviewModel);
      preloader.resolve(buildRatedByPath(vehicleParams), EditorialReviewModel);

      preloader.resolve(buildSimilarVehiclesCrossShopPath(modelLinkCode), CoreResearchModel);
      preloader.resolve(buildSafetyRatingsPathByParams({ params: vehicleParams }), SafetyModel);
      preloader.resolve(buildWhatsNewPath(vehicleParams), VehicleDetailsModel);
      preloader.resolve(buildPreProdContentPath(getParamsFromVehicle(makeModelSubmodelYear)), EditorialReviewModel);
      preloader.resolve(buildVideosPath(vehicleParams), MediaModel);
      preloader.resolve(buildSubmodelsPath(vehicleParams), VehicleModel);
      preloader.resolve(buildMmyModelFamilyIdsPath(vehicleParams), VehicleModel);
      preloader.resolve(buildForSaleYearsPath(vehicleParams), VehicleModel);

      // car-news widget
      preloader.resolve(`content["${CAR_NEWS_LANDING_CONTENT_PATH}"]`, CmsModel);
      preloader.resolve(buildMakeArticlesPath(make), EditorialModel);

      // page-links widget
      preloader.resolve(getPageLinksPath(), LinkWidgetsModel);

      if (CPO_CARS[make]?.[model]?.[year]) {
        preloader.resolve(`cpoData["${make}"]`, InventoryModel);
      }

      const adsList = [BUILD_PRICE_AD.AD_NAME, INCENTIVES_AD.AD_NAME];
      if (!mobile && year <= OLD_REVIEW_FORMAT_LAST_YEAR) {
        adsList.push(PRICING_AD.AD_NAME);
      }
      if (isABTestEas8839Enabled) {
        adsList.push(EAS_LISTING_AD.AD_NAME);
      }
      const vehiclePageContext = modelState.get('vehicle', PageModel);
      setAdsListToPreload(preloader, store.getState(), adsList, vehiclePageContext);

      // SEO photos
      preloader.resolve(buildSubmodelEvoxPhotoPath(photoParams), PhotosModel);
      preloader.resolve(
        buildMMYSubmodelTrimPhotosPathWithParams({
          ...photoParams,
          width: '175',
        }),
        PhotosModel
      );
      preloader.resolve(
        buildMMYSubmodelTrimPhotosPathWithParams({
          ...photoParams,
          width: '1600',
        }),
        PhotosModel
      );

      // Hero image
      const heroImageWidth = mobile ? CORE_GALLERY_SIZE.MOBILE.HERO.width : CORE_GALLERY_SIZE.DESKTOP.HERO.width;
      preloader.resolve(
        buildAllPhotosPath({
          ...photoParams,
          pageNum: 1,
          imageSize: heroImageWidth,
        }),
        PhotoflipperModel
      );

      // Gallery row
      if (!mobile) {
        preloader.resolve(
          buildAllPhotosPath({
            ...photoParams,
            pageNum: 1,
            imageSize: CORE_GALLERY_SIZE.DESKTOP.ROW.width,
          }),
          PhotoflipperModel
        );
      }

      // consumer-reviews
      preloader.set(ConsumerReviewsPaths.getVehicleFilterPath(), ConsumerReviewsModel, { params: vehicleParams });
      preloader.set(ConsumerReviewsPaths.getConsumerReviewsFilterPath(), ConsumerReviewsModel, {
        pagenum: 1,
        pagesize: 4, // Number of consumer reviews in component
      });
      preloader.resolve(ConsumerReviewsPaths.buildConsumerReviewsRatingsCountPath(vehicleParams), ConsumerReviewsModel);
      preloader.resolve(
        ConsumerReviewsPaths.buildConsumerReviewsRatingsCountPath({ ...vehicleParams, year: year - 1 }),
        ConsumerReviewsModel
      );
      preloader.resolve(ConsumerReviewsPaths.buildReviewsSummaryPath(vehicleParams), ConsumerReviewsModel);
      preloader.resolve(ConsumerReviewsPaths.getConsumerReviewsPath(), ConsumerReviewsModel);
      preloader.resolve(ConsumerReviewsPaths.buildConsumerReviewsAspectsPath(), ConsumerReviewsModel);

      const isCore6829Enabled =
        isInternational &&
        ExperimentUtil.getForcedOrAssignedRecipeName({
          state: store.getState(),
          campaignName: 'core-6829-used-nozip',
          defaultVal: 'ctrl',
        }) === 'chal';

      preloader.set('searchResultsFilter', InventoryModel, {
        ...getUnknownLocationSearchFilter(isCore6829Enabled),
        ...getInitialSearchFilter({ makeSlug: make, modelSlug: model, year }),
        ...getFilterFromLocation(query),
        pageSize: CORE_SRP_INVENTORY_COUNT[!!mobile],
      });
      preloader.resolve('searchResults', InventoryModel);

      const estimatedMileage = VEHICLE_MILEAGE_PER_YEAR * (new Date().getFullYear() - year);
      preloader.set('estimatedAppraisalMileage', VehicleModel, estimatedMileage);

      return preloader.load().then(async modelStateInstance => {
        const submodelAmounts = modelStateInstance.get(
          buildMMYSubmodelTypeAmountsPath(params),
          InventoryAggregateModel
        );
        const hasPriceRange = get(submodelAmounts, `priceRanges["${makeModelSubmodelYear.submodels.slug}"]`);

        if (isInternational || !hasPriceRange) {
          const usedBaseStyleId = modelStateInstance.get(
            `${getUsedBaseStylePath(params)}.usedDefaultStyle.id`,
            VehicleModel
          );

          if (usedBaseStyleId) {
            preloader.resolve(buildUsedTmvPricingPath(usedBaseStyleId), VehicleModel);
          }
        }

        // Other years
        const modelFamilyIds = modelStateInstance.get(
          buildMmyModelFamilyIdsPath({
            make,
            model,
            year,
          }),
          VehicleModel
        );
        if (!isEmpty(modelFamilyIds)) {
          preloader.resolve(buildModelFamilyIdsModelYearsPath(modelFamilyIds), VehicleModel);
        }

        // Range and Cost/EV Insights: start
        const stylesFeatures = modelStateInstance.get(
          FeatureSpecsPaths.buildPartialFeaturesPath(requestParams),
          VehicleModel
        );

        const popularStyles = modelStateInstance.get(
          buildPopularStylesPath(getParamsFromVehicle(makeModelSubmodelYear)),
          VehicleModel
        );
        const isElectric = !!get(stylesFeatures, '[0].styleAttributes.electric');
        const lowestPricedStyleId = get(minBy(popularStyles, 'price.baseMSRP') || popularStyles?.[0], 'id');

        preloader.resolve(buildFuelCostsPath(lowestPricedStyleId), VehicleModel);
        if (isElectric) {
          preloader.resolve(
            EvInsightsPaths.getEvInsightsDataPath(lowestPricedStyleId, INVENTORY_CODE.USED),
            EvInsightsModel
          );
          preloader.resolve(getWizardIncentivesPath(lowestPricedStyleId, true), IncentiveModel);

          if (year >= EV_TESTED_DATA_START_DATE) {
            preloader.resolve(buildEvTestedDataPath([lowestPricedStyleId]), VehicleModel);
          }
        }
        // Range and Cost/EV Insights: end

        // Features-highlights
        preloader.resolve(buildFuelCostsPath(get(stylesFeatures, '[0].id')), VehicleModel);

        if (
          get(stylesFeatures, '[0]styleAttributes.electric') ||
          get(stylesFeatures, '[0]styleAttributes.pluginElectric')
        ) {
          // TODO: Review after core-6558
          preloader.resolve(getEvTaxCreditAmountPath(get(stylesFeatures, '[0].id'), true), IncentiveModel);

          const isCore6558Enabled =
            ExperimentUtil.getForcedOrAssignedRecipeName({
              state: store.getState(),
              campaignName: 'core-6558-ev-specs',
              defaultVal: 'ctrl',
            }) === 'chal';
          if (isCore6558Enabled) {
            preloader.resolve(getWizardIncentivesPath(get(stylesFeatures, '[0].id'), true), IncentiveModel);
          }

          // treehouse promo
          preloader.resolve(getTreehouseEligiblePath(), TreehouseModel);
        }

        // Safety
        const editorialReview = modelStateInstance.get(buildReviewPath(params), EditorialReviewModel);
        if (isEmpty(get(editorialReview, 'safetyFeatures'))) {
          preloader.resolve(buildMMYGenericFeaturesPath(vehicleParams), VehicleModel);
        }

        const siteServedAds = modelStateInstance.get(
          getCreativeConfigAdPathByVehicle(mobile, vehiclePageContext),
          NativeAdsCreativeConfigModel
        );

        const hasEditorialSummary = !!editorialReview?.ratings?.overall?.text;

        store.dispatch(
          setCriticalCss({
            criticalCssHeight: 2050,
            ...siteServedAdsCriticalCss(siteServedAds, [INCENTIVES_AD.AD_NAME]),
            isInternational: !isCore6829Enabled && isInternational,
            hasEditorialSummary,
          })
        );

        if (isReviewSubpage) {
          preloader.set('server.preloadedAssets', PageModel, [
            getPreloadHeroImageAsset({
              modelState: modelStateInstance,
              makeModelSubmodelYear,
              photoParams,
              heroImageWidth,
              params,
              year,
              mobile,
            }),
          ]);
        }

        return preloader.load();
      });
    });
  },
  seo: state => {
    const { isReviewSubpage } = params;
    const modelState = getModelState(state);
    const makeModelSubmodelYear = modelState.get(buildVehiclePath(params), VehicleModel);
    const editorialReview = modelState.get(buildReviewPath(params), EditorialReviewModel);
    const editorialReviewAuthor = modelState.get(buildAuthorPath(params), EditorialReviewModel);
    const inventories = modelState.get(`vins`, InventoryModel);

    const seoObject = { jsonld: [], keywords: '' };

    if (makeModelSubmodelYear) {
      const mmySubmodelSlug = {
        make: makeModelSubmodelYear.make.slug,
        model: makeModelSubmodelYear.model.slug,
        submodel: makeModelSubmodelYear.submodels.slug,
        year: makeModelSubmodelYear.year,
      };
      const photoParams = {
        ...mmySubmodelSlug,
        submodel: makeModelSubmodelYear.submodels.name,
      };
      const consumerReviews = modelState.get(ConsumerReviewsPaths.getConsumerReviewsPath(), ConsumerReviewsModel);
      const displayVehicle = buildDisplayVehicle({ makeModelSubmodelYear, params, disableTrademarkCharacter: true });

      const metaTagsDisplayVehicle = buildDisplayVehicle({
        makeModelSubmodelYear,
        params,
        disableTrademarkCharacter: true,
        updateChevroletMakeName: true,
        usedPrefix: '',
      });

      if (isReviewSubpage) {
        const model = get(makeModelSubmodelYear, 'model.name', '');
        const make = get(makeModelSubmodelYear, 'make.name', '');
        seoObject.canonical = `https://www.edmunds.com/${params.make}/${params.model}/${params.year}/review/`;
        seoObject.title = `${metaTagsDisplayVehicle} Review & Ratings | Edmunds`;
        seoObject.description = `Edmunds' expert review of the Used ${metaTagsDisplayVehicle} provides the latest look at trim-level features and specs, performance, safety, and comfort. At Edmunds we drive every car we review, performing road tests and competitor comparisons to help you find your perfect car. Read the full in-depth review of the Used ${
          params.year
        } ${model} at Edmunds.`;
        seoObject.keywords = `${params.year} ${make} ${model} review`;
      } else {
        const amounts = modelState.get(buildMMYSubmodelTypeAmountsPath(params), InventoryAggregateModel);
        const maxSavings = amounts && isNumber(amounts.maxSavings) ? amounts.maxSavings : null;
        const inventoryCount = amounts && amounts.inventoryCount;

        const savingsStr =
          maxSavings && inventoryCount
            ? `Save up to ${formatPriceString(maxSavings)} on one of ${inventoryCount} ${displayVehicle} near you`
            : `Save money on ${displayVehicle} models near you`;

        seoObject.description = `Edmunds has detailed price information for the ${displayVehicle}. ${savingsStr}. Find detailed gas mileage information, insurance estimates, and more.`;
        seoObject.title = `${displayVehicle} Review | Edmunds`;
      }

      // Use original params for breadcrumbs
      seoObject.jsonld.push(
        getBreadcrumbSchema(
          buildCoreBreadcrumbs({ makeModelSubmodelYear, params, includeLastLink: true, isMainCorePage: true })
        )
      );

      const pageUrl = `${SITE_URL}${getReviewUrl({
        makeSlug: params.make,
        modelSlug: params.model,
        year: params.year,
      })}`;

      const priceRange = getUsedVehiclePriceRangeFromModelState({
        modelState,
        params,
        submodelSlug: makeModelSubmodelYear.submodels.slug,
      });

      const seoImages = modelState.get(
        buildMMYSubmodelTrimPhotosPathWithParams({
          ...photoParams,
          width: '1600',
        }),
        PhotosModel
      );
      const seoThumbs = modelState.get(
        buildMMYSubmodelTrimPhotosPathWithParams({
          ...photoParams,
          width: '175',
        }),
        PhotosModel
      );
      const thumb = get(seoThumbs, '[0]');
      const image = get(seoImages, '[0]');
      const thumbUrl = thumb && getMediaImageUrl(thumb.href);
      const imageUrl = image && getMediaImageUrl(image.href);

      const carSchema = appendReviewSchema(
        getCorePageCarSchema({
          makeModelSubmodelYear,
          editorialReview,
          consumerReviews,
          seoObject,
          priceRange,
          url: pageUrl,
          imageUrl,
          thumbUrl,
          isCorePage: true,
        }),
        {
          makeModelSubmodelYear,
          editorialReview,
          author: editorialReviewAuthor,
          consumerReviews,
          priceRange,
          hasItemReviewed: false,
          thumbUrl,
          isCorePage: true,
        }
      );

      seoObject.jsonld.push(carSchema);

      const photos = modelState.get(
        buildSubmodelEvoxPhotoPath({
          ...photoParams,
        }),
        PhotosModel
      );
      const photo = get(photos, '[0]');

      if (photo) {
        seoObject.jsonld.push(
          getImageSchema({
            uri: getMediaImageUrl(photo),
            title: displayVehicle,
          })
        );
      }
    }

    if (!isReviewSubpage) {
      if (inventories && Array.isArray(inventories.results)) {
        inventories.results.forEach(result => {
          const carVinSchema = getCarVinSchema({ vinData: result });
          delete carVinSchema.aggregateRating;
          seoObject.jsonld.push(carVinSchema);
        });
      }
    }

    return seoObject;
  },
  component: props => <UsedCore {...props} params={params} />,
  decorator: EdmundsNewCarsNoAdhesion,
  options: {
    disableScrollToTop: true,
    hasVehicleContext: true,
  },
});

export default params => corePageDecorator(getPageOptions(params), params);
